import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../api/api';
import { DataForm } from './forms';
import ContextMenu from './ContextMenu';
import * as XLSX from 'xlsx';
import './WellData.css';

const WellData = ({
    // Configuration props
    config: {
        tables, // { parent: 'BHA', child: 'BHA_Component' }
        identityColumns, // { parent: 'BHAID', child: 'ComponentID' }
        matchKey, // { parentKey: 'BHAID', childForeignKey: 'BHAID' }
        title = 'Well Data',
        icon = '🔧',
        parentIcon = '📋',
        childIcon = '⚙️',
        emptyParentMessage = 'No records found for this well',
        emptyChildMessage = 'No detail records found for this item',
        // Display configuration
        showTabs = true,
        tabs = [
            { key: 'details', label: 'Details', icon: '⚙️' },
            { key: 'image', label: 'Image', icon: '🖼️' }
        ],
        // Custom field mappings for display
        parentDisplayFields = [], // Override default fields to show in parent summary
        // Column filters
        hiddenParentFields = ['WellID'],
        hiddenChildFields = [],
        // Additional features
        enableInlineEdit = true,
        enableImageUpload = false,
        enableAttachments = false
    },
    // Well ID prop
    wellId,
    onWellChange,
    // Event handlers (optional)
    onParentSelect,
    onChildSelect,
    onParentSave,
    onChildSave,
    onParentDelete,
    onChildDelete,
    // Custom tab content renderer (optional)
    renderCustomTabContent
}) => {
    // Core state
    const [wells, setWells] = useState([]);
    const [selectedWellId, setSelectedWellId] = useState('');
    const [parentData, setParentData] = useState([]);
    const [childData, setChildData] = useState([]);
    const [selectedParent, setSelectedParent] = useState(null);
    
    // Column configurations
    const [parentColumns, setParentColumns] = useState([]);
    const [childColumns, setChildColumns] = useState([]);
    
    // UI state
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState('');
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, type: '', record: null });
    
    // Modal/Form state
    const [showParentForm, setShowParentForm] = useState(false);
    const [editingParentId, setEditingParentId] = useState(null);
    const [showConfigModal, setShowConfigModal] = useState(false);
    
    // Child editing state
    const [editingChildId, setEditingChildId] = useState(null);
    const [showChildForm, setShowChildForm] = useState(false);
    
    // Tab state
    const [activeTab, setActiveTab] = useState(showTabs ? tabs[0]?.key : 'details');
    
    // Configuration state for display fields
    const [parentDisplayConfig, setParentDisplayConfig] = useState([]);
    const [childDisplayConfig, setChildDisplayConfig] = useState([]);
    
    // Summary state for selected parent
    const [parentSummary, setParentSummary] = useState({});
    
    const role = localStorage.getItem('role') || 'admin';
    
    // Load parent data when well is selected
    const loadParentData = useCallback(async (wellId) => {
        if (!wellId) {
            setParentData([]);
            return;
        }

        try {
            setIsLoading(true);
            const response = await apiFetch(`/api/db/${tables.parent}?filter=WellID,eq,${wellId}`);
            setParentData(Array.isArray(response) ? response : []);
            
            // Clear selected parent and children when well changes
            setSelectedParent(null);
            setChildData([]);
        } catch (err) {
            console.error(`Failed to load ${tables.parent} data:`, err);
            setToast(`❌ Failed to load ${tables.parent} data`);
        } finally {
            setIsLoading(false);
        }
    }, [tables.parent]);

    // Sync with wellId prop
    useEffect(() => {
        if (wellId && wellId !== selectedWellId) {
            setSelectedWellId(wellId);
            loadParentData(wellId);
        }
    }, [wellId, selectedWellId, loadParentData]);

    // Helper function to convert column configuration to display configuration
    const convertColumnConfigToDisplayConfig = useCallback((columnConfig, tableName, hiddenFields = []) => {
        if (!columnConfig || !Array.isArray(columnConfig.columns)) {
            return [];
        }

        const result = columnConfig.columns
            .filter(col => {
                // Exclude identity columns and hidden fields
                const fieldName = col.name?.toLowerCase() || '';
                const isIdentityColumn = Object.values(identityColumns).some(id => 
                    fieldName === id.toLowerCase()
                );
                const isHidden = hiddenFields.some(hidden => 
                    fieldName === hidden.toLowerCase()
                );
                return !isIdentityColumn && !isHidden;
            })
            .map(col => {
                // Determine field type based on column type
                let fieldType = 'text'; // default
                const colType = col.type?.toLowerCase() || '';
                const colName = col.name?.toLowerCase() || '';
                
                if (colType.includes('int') || colType.includes('float') || colType.includes('decimal') || colType.includes('numeric') || colType === 'number') {
                    fieldType = 'number';
                } else if (colType.includes('date') || colType.includes('time')) {
                    fieldType = 'date';
                } else if (colName.includes('date') || colName.includes('time') || colName.includes('created') || colName.includes('modified')) {
                    fieldType = 'date';
                }

                return {
                    field: col.name,
                    label: col.label || col.name,
                    visible: true,
                    type: fieldType,
                    required: false,
                    maxLength: null,
                    columnType: col.type,
                    order: col.order
                };
            })
            .sort((a, b) => {
                // Sort by order if available, then by importance
                if (a.order && b.order) {
                    return a.order - b.order;
                }
                
                // Sort by importance: most useful fields first
                const getImportance = (field) => {
                    const fieldLower = field.toLowerCase();
                    
                    // Primary fields (most important)
                    if (fieldLower.includes('name') && !fieldLower.includes('filename')) return 1;
                    if (fieldLower.includes('type')) return 2;
                    if (fieldLower.includes('description')) return 3;
                    
                    // Secondary fields
                    if (fieldLower.includes('manufacturer')) return 10;
                    if (fieldLower.includes('serial')) return 11;
                    if (fieldLower.includes('length')) return 12;
                    if (fieldLower.includes('weight')) return 13;
                    if (fieldLower.includes('position')) return 14;
                    if (fieldLower.includes('depth')) return 15;
                    
                    // Technical fields
                    if (fieldLower.includes('size')) return 20;
                    if (fieldLower.includes('diameter')) return 21;
                    if (fieldLower.includes('material')) return 22;
                    if (fieldLower.includes('grade')) return 23;
                    
                    // ID fields
                    if (fieldLower.includes('id')) return 95;
                    
                    // Audit fields
                    if (fieldLower.includes('created')) return 100;
                    if (fieldLower.includes('modified')) return 101;
                    
                    return 50;
                };
                
                return getImportance(a.field) - getImportance(b.field);
            });
        
        return result;
    }, [identityColumns]);

    // Helper function to merge saved display config with current column config
    const mergeDisplayConfigs = useCallback((savedConfig, defaultConfig) => {
        if (!Array.isArray(savedConfig) || !Array.isArray(defaultConfig)) {
            return defaultConfig;
        }

        const savedConfigMap = new Map();
        savedConfig.forEach(config => {
            if (config.field) {
                savedConfigMap.set(config.field, config);
            }
        });

        return defaultConfig.map(defaultItem => {
            const savedItem = savedConfigMap.get(defaultItem.field);
            if (savedItem) {
                return {
                    ...defaultItem,
                    visible: savedItem.visible,
                    label: savedItem.label || defaultItem.label
                };
            }
            return defaultItem;
        });
    }, []);

    // Load child data when parent is selected
    const loadChildData = useCallback(async (parentId) => {
        if (!parentId) {
            setChildData([]);
            return;
        }

        try {
            const parentIdField = matchKey.childForeignKey;
            const response = await apiFetch(`/api/db/${tables.child}?filter=${parentIdField},eq,${parentId}`);
            setChildData(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error(`Failed to load ${tables.child} data:`, err);
            setToast(`❌ Failed to load ${tables.child} data`);
        }
    }, [tables.child, matchKey.childForeignKey]);

    // Calculate parent summary when child data changes
    useEffect(() => {
        if (childData && childData.length > 0) {
            // Calculate summary based on child records
            const summary = childData.reduce((acc, child) => {
                // Add generic calculations here
                acc.totalRecords = childData.length;
                
                // Add specific calculations based on common field names
                const length = parseFloat(child.Length) || 0;
                const weight = parseFloat(child.Weight) || 0;
                const od = parseFloat(child.OD) || 0;

                if (length > 0) {
                    acc.totalLength = (acc.totalLength || 0) + length;
                }
                if (weight > 0) {
                    acc.totalWeight = (acc.totalWeight || 0) + weight;
                }
                if (od > acc.maxOD) {
                    acc.maxOD = od;
                }
                
                return acc;
            }, { totalRecords: childData.length });

            setParentSummary(summary);
        } else {
            setParentSummary({ totalRecords: 0 });
        }
    }, [childData]);

    // Load initial data
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setIsLoading(true);
                const allWellsResponse = await apiFetch('/api/wells');
                const allConfigsResponse = await apiFetch('/api/config/columns');
                
                const allWells = Array.isArray(allWellsResponse) ? allWellsResponse : [];
                setWells(allWells);

                // Find table configurations
                const parentConfig = Array.isArray(allConfigsResponse) ? 
                    allConfigsResponse.find(table => table.tableName === tables.parent) : null;
                const childConfig = Array.isArray(allConfigsResponse) ? 
                    allConfigsResponse.find(table => table.tableName === tables.child) : null;

                setParentColumns(parentConfig?.columns || []);
                setChildColumns(childConfig?.columns || []);
                
                // Debug logging for column configurations
                console.log(`WellData - Loaded column configurations:`, {
                    parentTable: tables.parent,
                    parentConfigFound: !!parentConfig,
                    parentColumnsCount: (parentConfig?.columns || []).length,
                    childTable: tables.child,
                    childConfigFound: !!childConfig,
                    childColumnsCount: (childConfig?.columns || []).length
                });

                if (!parentConfig) {
                    console.warn(`WellData - No column configuration found for parent table: ${tables.parent}`);
                }
                if (!childConfig) {
                    console.warn(`WellData - No column configuration found for child table: ${tables.child}`);
                }

                // Generate display configurations
                const defaultParentDisplayConfig = convertColumnConfigToDisplayConfig(
                    parentConfig, 
                    tables.parent, 
                    hiddenParentFields
                );
                const defaultChildDisplayConfig = convertColumnConfigToDisplayConfig(
                    childConfig, 
                    tables.child, 
                    hiddenChildFields
                );
                
                // Load saved configurations
                const storageKeyParent = `${tables.parent.toLowerCase()}DisplayConfig`;
                const storageKeyChild = `${tables.child.toLowerCase()}DisplayConfig`;
                
                const savedParentConfig = localStorage.getItem(storageKeyParent);
                const savedChildConfig = localStorage.getItem(storageKeyChild);
                
                if (savedParentConfig) {
                    try {
                        const parsed = JSON.parse(savedParentConfig);
                        const merged = mergeDisplayConfigs(parsed, defaultParentDisplayConfig);
                        setParentDisplayConfig(merged);
                    } catch (err) {
                        console.warn('Failed to parse saved parent config:', err);
                        setParentDisplayConfig(defaultParentDisplayConfig);
                    }
                } else {
                    setParentDisplayConfig(defaultParentDisplayConfig);
                }
                
                if (savedChildConfig) {
                    try {
                        const parsed = JSON.parse(savedChildConfig);
                        const merged = mergeDisplayConfigs(parsed, defaultChildDisplayConfig);
                        setChildDisplayConfig(merged);
                    } catch (err) {
                        console.warn('Failed to parse saved child config:', err);
                        setChildDisplayConfig(defaultChildDisplayConfig);
                    }
                } else {
                    setChildDisplayConfig(defaultChildDisplayConfig);
                }

            } catch (err) {
                console.error('Failed to load initial data:', err);
                setToast('❌ Failed to load initial data: ' + err.message);
                setParentDisplayConfig([]);
                setChildDisplayConfig([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadInitialData();
    }, [tables.parent, tables.child, convertColumnConfigToDisplayConfig, hiddenParentFields, hiddenChildFields, mergeDisplayConfigs]);

    // Handle well selection
    const handleWellChange = useCallback((e) => {
        const wellId = e.target.value;
        setSelectedWellId(wellId);
        loadParentData(wellId);
        if (onWellChange) {
            onWellChange(wellId);
        }
    }, [loadParentData, onWellChange]);

    // Handle parent selection
    const handleParentSelect = useCallback((parent) => {
        setSelectedParent(parent);
        const parentId = parent[identityColumns.parent];
        loadChildData(parentId);
        
        // Call external handler if provided
        if (onParentSelect) {
            onParentSelect(parent);
        }
    }, [loadChildData, identityColumns.parent, onParentSelect]);

    // Context menu handlers
    const handleContextMenu = useCallback((e, type, record) => {
        e.preventDefault();
        
        if (role === 'admin' || role === 'read-write') {
            setContextMenu({
                visible: true,
                x: e.clientX,
                y: e.clientY,
                type,
                record
            });
        }
    }, [role]);

    const handleCloseContextMenu = useCallback(() => {
        setContextMenu({ visible: false, x: 0, y: 0, type: '', record: null });
    }, []);

    // CRUD operations for parent
    const handleAddParent = useCallback(() => {
        if (!selectedWellId) {
            setToast('Please select a well first');
            return;
        }
        
        if (!parentColumns || parentColumns.length === 0) {
            setToast('❌ Column configuration not loaded. Please refresh the page.');
            console.error('WellData - Attempted to add parent without column configuration loaded');
            return;
        }
        
        setEditingParentId(null);
        setShowParentForm(true);
        handleCloseContextMenu();
    }, [selectedWellId, parentColumns, handleCloseContextMenu]);

    const handleEditParent = useCallback((parent) => {
        if (!parentColumns || parentColumns.length === 0) {
            setToast('❌ Column configuration not loaded. Please refresh the page.');
            console.error('WellData - Attempted to edit parent without column configuration loaded');
            return;
        }
        
        const parentId = parent[identityColumns.parent];
        setEditingParentId(parentId);
        setShowParentForm(true);
        handleCloseContextMenu();
    }, [handleCloseContextMenu, identityColumns.parent, parentColumns]);

    const handleDeleteParent = useCallback(async (parent) => {
        const parentName = parent[Object.keys(parent).find(key => key.toLowerCase().includes('name'))] || 
                           `${tables.parent} ${parent[identityColumns.parent]}`;
        
        if (!window.confirm(`Are you sure you want to delete "${parentName}"?\n\n⚠️ This will also delete all associated detail records.`)) {
            return;
        }

        try {
            const parentId = parent[identityColumns.parent];
            await apiFetch(`/api/db/${tables.parent}/${parentId}`, { method: 'DELETE' });
            setToast('✅ Record deleted successfully');
            loadParentData(selectedWellId);
            
            // Clear selection if deleted parent was selected
            if (selectedParent && selectedParent[identityColumns.parent] === parentId) {
                setSelectedParent(null);
                setChildData([]);
            }
            
            if (onParentDelete) {
                onParentDelete(parent);
            }
        } catch (err) {
            console.error('Failed to delete parent:', err);
            setToast('❌ Failed to delete record');
        }
        handleCloseContextMenu();
    }, [tables.parent, identityColumns.parent, selectedWellId, selectedParent, loadParentData, onParentDelete, handleCloseContextMenu]);

    // CRUD operations for child
    const handleEditChild = useCallback((child) => {
        if (!childColumns || childColumns.length === 0) {
            setToast('❌ Column configuration not loaded. Please refresh the page.');
            console.error('WellData - Attempted to edit child without column configuration loaded');
            return;
        }
        
        const childId = child[identityColumns.child];
        setEditingChildId(childId);
        setShowChildForm(true);
        handleCloseContextMenu();
    }, [handleCloseContextMenu, identityColumns.child, childColumns]);

    const handleDeleteChild = useCallback(async (child) => {
        const childName = child[Object.keys(child).find(key => key.toLowerCase().includes('name'))] || 
                          child.Description || 
                          `${tables.child} ${child[identityColumns.child]}`;
        
        if (!window.confirm(`Are you sure you want to delete "${childName}"?`)) {
            return;
        }

        try {
            const childId = child[identityColumns.child];
            await apiFetch(`/api/db/${tables.child}/${childId}`, { method: 'DELETE' });
            setToast('✅ Record deleted successfully');
            
            // Reload child data
            const parentId = selectedParent[identityColumns.parent];
            await loadChildData(parentId);
            
            if (onChildDelete) {
                onChildDelete(child);
            }
        } catch (err) {
            console.error('Failed to delete child:', err);
            setToast('❌ Failed to delete record');
        }
        handleCloseContextMenu();
    }, [tables.child, identityColumns.child, selectedParent, identityColumns.parent, loadChildData, onChildDelete, handleCloseContextMenu]);



    // Form save handlers
    const handleParentSave = useCallback(async (result) => {
        try {
            const { parentId } = result;
            setToast('✅ Records saved successfully');
            setShowParentForm(false);
            setEditingParentId(null);
            
            // Reload parent data
            await loadParentData(selectedWellId);
            
            // If we created a new parent, select it
            if (!editingParentId && parentId) {
                const updatedParentData = await apiFetch(`/api/db/${tables.parent}?filter=WellID,eq,${selectedWellId}`);
                const newParent = Array.isArray(updatedParentData) ? 
                    updatedParentData.find(p => p[identityColumns.parent] === parentId) : null;
                if (newParent) {
                    handleParentSelect(newParent);
                }
            }
            
            if (onParentSave) {
                onParentSave(result);
            }
        } catch (err) {
            console.error('Failed to save parent:', err);
            setToast('❌ Failed to save: ' + err.message);
        }
    }, [selectedWellId, editingParentId, loadParentData, tables.parent, identityColumns.parent, handleParentSelect, onParentSave]);

    const handleChildSave = useCallback(async (result) => {
        try {
            setToast('✅ Record saved successfully');
            setShowChildForm(false);
            setEditingChildId(null);
            
            // Reload child data
            const parentId = selectedParent[identityColumns.parent];
            await loadChildData(parentId);
            
            if (onChildSave) {
                onChildSave(result);
            }
        } catch (err) {
            console.error('Failed to save child:', err);
            setToast('❌ Failed to save: ' + err.message);
        }
    }, [selectedParent, identityColumns.parent, loadChildData, onChildSave]);

    // Context menu options
    const contextMenuOptions = useMemo(() => {
        const { type, record } = contextMenu;
        
        if (type === 'parent') {
            return [
                {
                    type: 'header',
                    label: `${tables.parent} Actions`,
                    icon: parentIcon
                },
                {
                    type: 'divider'
                },
                {
                    type: 'action',
                    label: `Edit ${tables.parent}`,
                    icon: '✏️',
                    onClick: () => handleEditParent(record)
                },
                {
                    type: 'action',
                    label: `Delete ${tables.parent}`,
                    icon: '🗑️',
                    onClick: () => handleDeleteParent(record)
                }
            ];
        }
        
        if (type === 'child') {
            const options = [
                {
                    type: 'header',
                    label: `${tables.child} Actions`,
                    icon: childIcon
                },
                {
                    type: 'divider'
                }
            ];
            
            options.push({
                type: 'action',
                label: 'Edit',
                icon: '✏️',
                onClick: () => handleEditChild(record)
            });
            
            options.push({
                type: 'divider'
            }, {
                type: 'action',
                label: `Delete ${tables.child}`,
                icon: '🗑️',
                onClick: () => handleDeleteChild(record)
            });
            
            return options;
        }
        
        return [];
    }, [contextMenu, tables, parentIcon, childIcon, handleEditParent, handleDeleteParent, handleEditChild, handleDeleteChild]);

    // Utility functions
    const formatFieldValue = (value, type) => {
        if (value === null || value === undefined || value === '') return '-';
        
        switch (type) {
            case 'number':
                return typeof value === 'number' ? value.toLocaleString() : value;
            case 'date':
                return new Date(value).toLocaleDateString();
            default:
                return value;
        }
    };

    // Excel export function
    const handleExportToExcel = useCallback(() => {
        if (!selectedParent) {
            setToast('Please select a parent record first');
            return;
        }

        try {
            // Create a new workbook
            const workbook = XLSX.utils.book_new();

            // Prepare parent data
            const parentHeaders = parentDisplayConfig
                .filter(col => col.visible)
                .map(col => col.label);
            
            const parentRow = parentDisplayConfig
                .filter(col => col.visible)
                .map(col => {
                    const value = selectedParent[col.field];
                    if (col.type === 'date' && value) {
                        return new Date(value).toLocaleDateString();
                    } else if (col.type === 'number' && value !== null && value !== undefined) {
                        return typeof value === 'number' ? value : parseFloat(value) || value;
                    }
                    return value || '';
                });

            // Create parent worksheet
            const parentData = [parentHeaders, parentRow];
            const parentWorksheet = XLSX.utils.aoa_to_sheet(parentData);
            
            // Set column widths for parent sheet
            const parentColWidths = parentHeaders.map(() => ({ wch: 15 }));
            parentWorksheet['!cols'] = parentColWidths;
            
            XLSX.utils.book_append_sheet(workbook, parentWorksheet, tables.parent);

            // Prepare child data if exists
            if (childData && childData.length > 0) {
                const childHeaders = childDisplayConfig
                    .filter(col => col.visible)
                    .map(col => col.label);
                
                const childRows = childData.map(child => 
                    childDisplayConfig
                        .filter(col => col.visible)
                        .map(col => {
                            const value = child[col.field];
                            if (col.type === 'date' && value) {
                                return new Date(value).toLocaleDateString();
                            } else if (col.type === 'number' && value !== null && value !== undefined) {
                                return typeof value === 'number' ? value : parseFloat(value) || value;
                            }
                            return value || '';
                        })
                );

                // Create child worksheet
                const childDataForSheet = [childHeaders, ...childRows];
                const childWorksheet = XLSX.utils.aoa_to_sheet(childDataForSheet);
                
                // Set column widths for child sheet
                const childColWidths = childHeaders.map(() => ({ wch: 15 }));
                childWorksheet['!cols'] = childColWidths;
                
                XLSX.utils.book_append_sheet(workbook, childWorksheet, tables.child);
            }

            // Generate filename
            const selectedWell = wells.find(w => w.WellID == selectedWellId);
            const wellName = selectedWell ? (selectedWell.WellName || selectedWell.Name || selectedWell.name) : 'Well';
            const parentName = selectedParent[Object.keys(selectedParent).find(key => key.toLowerCase().includes('name'))] || 
                               `${tables.parent}_${selectedParent[identityColumns.parent]}`;
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
            const filename = `${wellName}_${parentName}_${timestamp}.xlsx`;

            // Save the file
            XLSX.writeFile(workbook, filename);
            
            setToast(`✅ Exported to ${filename}`);
        } catch (err) {
            console.error('Export failed:', err);
            setToast('❌ Export failed: ' + err.message);
        }
    }, [selectedParent, childData, parentDisplayConfig, childDisplayConfig, tables, wells, selectedWellId, identityColumns.parent]);



    // Toast management
    const clearToast = useCallback(() => setToast(''), []);

    useEffect(() => {
        if (toast) {
            const timer = setTimeout(clearToast, 2000);
            return () => clearTimeout(timer);
        }
    }, [toast, clearToast]);

    // Get selected well name for display
    const selectedWell = wells.find(w => w.WellID == selectedWellId);
    const selectedWellName = selectedWell ? selectedWell.WellName || selectedWell.Name || selectedWell.name : '';

    // Render functions
    const renderWellSelector = () => (
        <div className="combined-selectors-section">
            <div className="selectors-row">
                <div className="selector-group">
                    <label htmlFor="well-select">Well:</label>
                    <select
                        id="well-select"
                        value={selectedWellId}
                        onChange={handleWellChange}
                        className="form-select"
                        disabled={isLoading}
                    >
                        <option value="">Select a well...</option>
                        {wells.map(well => (
                            <option key={well.WellID} value={well.WellID}>
                                {well.WellName || well.Name || well.name || `Well ${well.WellID}`}
                            </option>
                        ))}
                    </select>
                </div>
                
                <div className="selector-group">
                    <label htmlFor="parent-select">{tables.parent}:</label>
                    <select
                        id="parent-select"
                        value={selectedParent?.[identityColumns.parent] || ''}
                        onChange={(e) => {
                            const parentId = e.target.value;
                            if (parentId) {
                                const parent = parentData.find(p => p[identityColumns.parent] == parentId);
                                if (parent) {
                                    handleParentSelect(parent);
                                }
                            } else {
                                setSelectedParent(null);
                                setChildData([]);
                            }
                        }}
                        className="form-select"
                        disabled={!selectedWellId || parentData.length === 0}
                    >
                        <option value="">Select a {tables.parent}...</option>
                        {parentData.map(parent => {
                            const parentId = parent[identityColumns.parent];
                            const parentName = parent[Object.keys(parent).find(key => key.toLowerCase().includes('name'))] || 
                                               `${tables.parent} #${parentId}`;
                            return (
                                <option key={parentId} value={parentId}>
                                    {parentName}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </div>
        </div>
    );

    const renderQuickActions = () => (
        <div className="quick-actions">
            <button
                className="quick-action-btn small"
                onClick={handleAddParent}
                title={`Add New ${tables.parent}`}
                disabled={!selectedWellId}
            >
                {parentIcon} Add {tables.parent}
            </button>
            {selectedParent && (role === 'admin' || role === 'read-write') && (
                <>
                    <button
                        className="quick-action-btn small"
                        onClick={() => handleEditParent(selectedParent)}
                        title={`Edit ${tables.parent}`}
                    >
                        ✏️ Edit
                    </button>
                    <button
                        className="quick-action-btn small"
                        onClick={() => handleDeleteParent(selectedParent)}
                        title={`Delete ${tables.parent}`}
                    >
                        🗑️ Delete
                    </button>
                </>
            )}
            {selectedParent && (
                <button
                    className="quick-action-btn small"
                    onClick={handleExportToExcel}
                    title="Export to Excel"
                >
                    📊 Export Excel
                </button>
            )}
            <button
                className="quick-action-btn small"
                onClick={() => setShowConfigModal(true)}
                title="Configure Display"
            >
                ⚙️ Configure
            </button>
        </div>
    );

    const renderParentSummary = () => {
        if (!parentSummary.totalRecords) return null;
        
        return (
            <div className="parent-summary-compact">
                <div className="summary-items-horizontal">
                    <div className="summary-item-compact">
                        <span className="summary-icon">{childIcon}</span>
                        <span className="summary-label">Records:</span>
                        <span className="summary-value">{parentSummary.totalRecords}</span>
                    </div>
                    {parentSummary.totalLength > 0 && (
                        <div className="summary-item-compact">
                            <span className="summary-icon">📏</span>
                            <span className="summary-label">Total Length:</span>
                            <span className="summary-value">{parentSummary.totalLength.toFixed(2)} m</span>
                        </div>
                    )}
                    {parentSummary.totalWeight > 0 && (
                        <div className="summary-item-compact">
                            <span className="summary-icon">⚖️</span>
                            <span className="summary-label">Total Weight:</span>
                            <span className="summary-value">{parentSummary.totalWeight.toFixed(2)} kg</span>
                        </div>
                    )}
                    {parentSummary.maxOD > 0 && (
                        <div className="summary-item-compact">
                            <span className="summary-icon">📐</span>
                            <span className="summary-label">Max OD:</span>
                            <span className="summary-value">{parentSummary.maxOD.toFixed(3)} in</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderParentRecords = () => {
        if (!selectedWellId) {
            return (
                <div className="records-placeholder">
                    <div className="placeholder-icon">{parentIcon}</div>
                    <p>Select a well to view {tables.parent} records</p>
                </div>
            );
        }

        if (parentData.length === 0) {
            return (
                <div className="empty-state">
                    <div className="empty-icon">{parentIcon}</div>
                    <p>{emptyParentMessage}</p>
                    {(role === 'admin' || role === 'read-write') && (
                        <button className="add-btn" onClick={handleAddParent}>
                            ➕ Add First {tables.parent}
                        </button>
                    )}
                </div>
            );
        }

        // Filter records to show only selected parent if one is selected
        const displayedParentData = selectedParent ? [selectedParent] : parentData;
        const recordsCountText = selectedParent ? 
            `Showing selected record (1 of ${parentData.length} total)` : 
            `${parentData.length} records found`;

        return (
            <div className="parent-records">
                <div className="records-table-container">
                    <table className="records-table">
                        <thead>
                            <tr>
                                {parentDisplayConfig.map(col => col.visible && (
                                    <th key={col.field} className={col.type === 'number' ? 'numeric' : ''}>{col.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {displayedParentData.map(parent => {
                                const parentId = parent[identityColumns.parent];
                                const isSelected = selectedParent && selectedParent[identityColumns.parent] === parentId;
                                
                                return (
                                    <tr 
                                        key={parentId}
                                        className={`record-row parent-record-row ${isSelected ? 'selected' : ''}`}
                                        onContextMenu={(e) => handleContextMenu(e, 'parent', parent)}
                                        onClick={() => handleParentSelect(parent)}
                                        title="Click to select, right-click for options"
                                        style={{
                                            fontWeight: 'bold',
                                            borderLeft: 'none',
                                            position: 'relative'
                                        }}
                                    >
                                        {parentDisplayConfig.map(col => col.visible && (
                                            <td key={col.field} className={`record-cell parent-record-cell ${col.type === 'number' ? 'numeric' : ''}`}>
                                                {formatFieldValue(parent[col.field], col.type)}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderChildRecords = () => {
        if (!selectedParent) {
            return (
                <div className="records-placeholder">
                    <div className="placeholder-icon">{childIcon}</div>
                    <p>Select a {tables.parent} to view its {tables.child} records</p>
                </div>
            );
        }

        return (
            <div className="child-records">

                {childData.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">{childIcon}</div>
                        <p>{emptyChildMessage}</p>
                        <p>Use "Edit {tables.parent}" to add {tables.child} records</p>
                    </div>
                ) : (
                    <div className="records-table-container">
                        <table className="records-table">
                            <thead>
                                <tr>
                                    {childDisplayConfig.map(col => col.visible && (
                                        <th key={col.field} className={col.type === 'number' ? 'numeric' : ''}>{col.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {childData.map(child => {
                                    const childId = child[identityColumns.child];
                                    
                                    return (
                                        <tr 
                                            key={childId}
                                            className="record-row"
                                            onContextMenu={(e) => handleContextMenu(e, 'child', child)}
                                            title="Right-click for options"
                                        >
                                            {childDisplayConfig.map(col => col.visible && (
                                                <td key={col.field} className={`record-cell ${col.type === 'number' ? 'numeric' : ''}`}>
                                                    {formatFieldValue(child[col.field], col.type)}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    const renderTabs = () => {
        if (!showTabs || !selectedParent) return null;

        return (
            <div className="tabs">
                {tabs.map(tab => (
                                    <button 
                    key={tab.key}
                    className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                        width: 'auto',
                        flex: 'none',
                        minWidth: 'auto'
                    }}
                >
                    {tab.icon} {tab.label}
                    {tab.key === 'details' && ` (${childData.length})`}
                    {tab.key !== 'details' && tab.showCount && ` (${tab.count || 0})`}
                </button>
                ))}
            </div>
        );
    };

    const renderTabContent = () => {
        if (!selectedParent) return null;

        // Check if there's a custom tab content renderer
        if (renderCustomTabContent) {
            const customContent = renderCustomTabContent(activeTab, selectedParent, childData, selectedWellId);
            if (customContent !== null) {
                return customContent;
            }
        }

        switch (activeTab) {
            case 'details':
                return renderChildRecords();
            case 'image':
                return (
                    <div className="image-placeholder">
                        <div className="placeholder-icon">🖼️</div>
                        <p>Image functionality not implemented in base component</p>
                        <p>Override this in the parent component for custom image handling</p>
                    </div>
                );
            default:
                return renderChildRecords();
        }
    };

    return (
        <div className="welldata-container fullscreen">
            {/* Header Controls */}
            <div className="welldata-header">
                <div className="header-main">
                    <h1>{icon} {title}</h1>
                    {renderWellSelector()}
                </div>
                
                {renderQuickActions()}
            </div>

            {/* Main Content */}
            {isLoading ? (
                <div className="loading-indicator">
                    <div className="spinner"></div>
                    <p>Loading...</p>
                </div>
            ) : (
                <div className="welldata-content-vertical">
                    {/* Parent Records Section */}
                    <div className="parent-records-section">
                        {renderParentRecords()}
                    </div>

                    {/* Child Records Section */}
                    <div className="child-records-section">
                        {selectedParent ? (
                            <div className="details-container">
                                {renderTabs()}

                                <div className="tab-content">
                                    {activeTab === 'details' && (
                                        <div className="header-summary">
                                            {renderParentSummary()}
                                        </div>
                                    )}
                                    {renderTabContent()}
                                </div>
                            </div>
                        ) : (
                            <div className="placeholder-container">
                                <div className="placeholder-content">
                                    <div className="placeholder-icon">{parentIcon}</div>
                                    <h3>Select a {tables.parent} to View {tables.child}</h3>
                                    <p>Choose a {tables.parent} from the dropdown above or click on a record to see its details.</p>
                                    {!selectedWellId && (
                                        <p><strong>First select a well from the dropdown at the top.</strong></p>
                                    )}
                                    {selectedWellId && parentData.length === 0 && (
                                        <div className="no-data-actions">
                                            <p><strong>No {tables.parent} records found for this well.</strong></p>
                                            {(role === 'admin' || role === 'read-write') && (
                                                <button className="add-btn" onClick={handleAddParent}>
                                                    ➕ Add First {tables.parent}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modals */}
            {showParentForm && (
                <div className="modal-overlay">
                    <div className="modal-content large-modal">
                        {parentColumns && parentColumns.length > 0 ? (
                            <DataForm
                                tables={tables}
                                identityColumns={identityColumns}
                                matchKey={matchKey}
                                recordId={editingParentId}
                                defaultParentData={editingParentId ? {} : { WellID: selectedWellId }}
                                hiddenParentFields={hiddenParentFields}
                                parentColumns={parentColumns}
                                childColumns={childColumns}
                                columnsPerRow={4}
                                skipAutoSave={true}
                                onSave={handleParentSave}
                                onCancel={() => {
                                    setShowParentForm(false);
                                    setEditingParentId(null);
                                }}
                                title={`${title}${selectedWellName ? ' - ' + selectedWellName : ''}`}
                            />
                        ) : (
                            <div className="modal-loading">
                                <div className="spinner"></div>
                                <p>Loading column configuration...</p>
                                <button 
                                    className="cancel-btn"
                                    onClick={() => {
                                        setShowParentForm(false);
                                        setEditingParentId(null);
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showChildForm && selectedParent && (
                <div className="modal-overlay">
                    <div className="modal-content large-modal">
                        {childColumns && childColumns.length > 0 ? (
                            <DataForm
                                tables={{ parent: tables.parent, child: tables.child }}
                                identityColumns={identityColumns}
                                matchKey={matchKey}
                                recordId={editingChildId}
                                defaultParentData={selectedParent}
                                defaultChildData={editingChildId ? {} : { [matchKey.childForeignKey]: selectedParent[identityColumns.parent] }}
                                hiddenParentFields={[...hiddenParentFields, identityColumns.parent]}
                                hiddenChildFields={[...hiddenChildFields, matchKey.childForeignKey]}
                                parentColumns={parentColumns}
                                childColumns={childColumns}
                                focusOnChild={true}
                                columnsPerRow={4}
                                skipAutoSave={true}
                                onSave={handleChildSave}
                                onCancel={() => {
                                    setShowChildForm(false);
                                    setEditingChildId(null);
                                }}
                                title={`Edit ${tables.child}${selectedWellName ? ' - ' + selectedWellName : ''}`}
                            />
                        ) : (
                            <div className="modal-loading">
                                <div className="spinner"></div>
                                <p>Loading column configuration...</p>
                                <button 
                                    className="cancel-btn"
                                    onClick={() => {
                                        setShowChildForm(false);
                                        setEditingChildId(null);
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Configuration Modal */}
            {showConfigModal && (
                <div className="modal-overlay">
                    <div className="modal-content config-modal">
                        <div className="modal-header">
                            <h3>Configure Table Columns</h3>
                            <button 
                                className="close-btn"
                                onClick={() => setShowConfigModal(false)}
                            >
                                ✕
                            </button>
                        </div>
                        
                        <div className="config-content">
                            <div className="config-section">
                                <h4>{tables.parent} Table Columns</h4>
                                <div className="config-fields">
                                    {parentDisplayConfig.map((col, index) => (
                                        <div key={col.field} className="config-field">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={col.visible}
                                                    onChange={(e) => {
                                                        const newConfig = [...parentDisplayConfig];
                                                        newConfig[index].visible = e.target.checked;
                                                        setParentDisplayConfig(newConfig);
                                                    }}
                                                />
                                                <span className="field-label">{col.label}</span>
                                                <span className="field-type">({col.type})</span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="config-section">
                                <h4>{tables.child} Table Columns</h4>
                                <div className="config-fields">
                                    {childDisplayConfig.map((col, index) => (
                                        <div key={col.field} className="config-field">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={col.visible}
                                                    onChange={(e) => {
                                                        const newConfig = [...childDisplayConfig];
                                                        newConfig[index].visible = e.target.checked;
                                                        setChildDisplayConfig(newConfig);
                                                    }}
                                                />
                                                <span className="field-label">{col.label}</span>
                                                <span className="field-type">({col.type})</span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button 
                                className="save-config-btn"
                                onClick={() => {
                                    const storageKeyParent = `${tables.parent.toLowerCase()}DisplayConfig`;
                                    const storageKeyChild = `${tables.child.toLowerCase()}DisplayConfig`;
                                    localStorage.setItem(storageKeyParent, JSON.stringify(parentDisplayConfig));
                                    localStorage.setItem(storageKeyChild, JSON.stringify(childDisplayConfig));
                                    setToast('✅ Display configuration saved');
                                    setShowConfigModal(false);
                                }}
                            >
                                💾 Save Configuration
                            </button>
                            <button 
                                className="cancel-btn"
                                onClick={() => setShowConfigModal(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Context Menu */}
            <ContextMenu
                x={contextMenu.x}
                y={contextMenu.y}
                isVisible={contextMenu.visible}
                onClose={handleCloseContextMenu}
                options={contextMenuOptions}
            />

            {/* Toast Notification */}
            {toast && (
                <div className="toast-notification">
                    {toast}
                </div>
            )}
        </div>
    );
};

export default WellData; 